import { Component, EventEmitter, Input, OnChanges, Output, ViewEncapsulation } from "@angular/core";
import { ProductExposureApiService, ProductExposureSummaryDTO } from "./product-exposure-api.service";

/**
 * What is in flight on a style's, or a feature choice's, products in Uniformax and Proximity.
 * Information only: it refuses nothing. Set productTypeId or featureChoiceId.
 *
 * Gatekeeper step P0.3; see client/.claude/docs/product-change-gatekeeper.md. The numbers come
 * from ProductExposureService, the same service the change gate will use.
 */
@Component({
  selector: "app-product-exposure-panel",
  template: `
    <div class="qb-detail-card overflow-auto">
      <!-- Closed by default: the header line carries the headline. -->
      <div
        class="flex items-baseline gap-2 cursor-pointer select-none"
        title="Show or hide the Product Use details"
        (click)="isOpen = !isOpen"
      >
        <i class="pi text-xs" [ngClass]="isOpen ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
        <h6 class="font-semibold m-0 shrink-0">Product Use</h6>

        <div class="text-sm min-w-0">
          <span *ngIf="isLoading" style="color: var(--p-text-muted-color, #6b7280)">
            <i class="pi pi-spinner pi-spin text-xs"></i>
            The system counts the orders, purchase orders, and items.
          </span>

          <span *ngIf="!isLoading && loadError" style="color: var(--p-red-500, #ef4444)">
            The system cannot get the counts. Select Refresh to try again.
          </span>

          <!-- "Used" is at any time, shipped and closed included, so the open counts below can all be 0. -->
          <ng-container *ngIf="!isLoading && !loadError && x">
            <ng-container *ngIf="x.ProductCount === 0">{{ noun }} has no products.</ng-container>
            <ng-container *ngIf="x.ProductCount > 0 && x.UsedProducts > 0">
              <span class="font-semibold" style="color: var(--p-orange-600, #ea580c)">
                {{ x.UsedProducts }} of {{ x.ProductCount }} products were used on orders, purchase orders, or items.
              </span>
              If you change the features or choices of these products, the old and open records also change.
            </ng-container>
            <ng-container *ngIf="x.ProductCount > 0 && x.UsedProducts === 0 && x.UnknownProducts > 0">
              Uniformax did not use these products. The system cannot check Proximity.
            </ng-container>
            <ng-container *ngIf="x.ProductCount > 0 && x.UsedProducts === 0 && x.UnknownProducts === 0">
              <span style="color: var(--p-green-600, #16a34a)">&#10003;</span>
              No product was used in Uniformax or Proximity.
            </ng-container>
          </ng-container>
        </div>

        <a
          *ngIf="!isLoading"
          class="ml-auto text-sm cursor-pointer text-primary shrink-0"
          title="Count again"
          (click)="$event.stopPropagation(); refresh()"
          >Refresh</a
        >
      </div>

      <ng-container *ngIf="isOpen && !isLoading && !loadError && x">
        <div *ngIf="featureChoiceId != null && x.ProductCount > 0" class="text-sm mt-2" style="color: var(--p-text-muted-color, #6b7280)">
          This choice is on {{ x.ProductCount }} products in {{ x.StyleCount }} styles.
        </div>

        <div *ngIf="x.ProductCount > 0" class="flex gap-4 mt-2">
          <div class="flex-1">
            <div class="text-sm font-semibold mb-1">Uniformax</div>
            <table class="qb-info-table compact flush">
              <tr>
                <td class="label">Job order lines to ship</td>
                <td class="value text-right">{{ x.OwedLines }}</td>
              </tr>
              <tr *ngIf="x.OwedLinesNeverOrdered > 0">
                <td class="label" style="color: var(--p-red-500, #ef4444)">Lines with no purchase order</td>
                <td class="value text-right" style="color: var(--p-red-500, #ef4444)">{{ x.OwedLinesNeverOrdered }}</td>
              </tr>
              <tr>
                <td class="label">Purchase order lines to receive</td>
                <td class="value text-right">{{ x.OpenPoLines }}</td>
              </tr>
              <tr>
                <td class="label">Units in stock or in process</td>
                <td class="value text-right">{{ x.ItemsHeld }}</td>
              </tr>
              <tr *ngIf="x.AccountOwnedItemsHeld > 0">
                <td class="label">Units the account owns</td>
                <td class="value text-right">{{ x.AccountOwnedItemsHeld }}</td>
              </tr>
              <tr>
                <td class="label">Products used at any time</td>
                <td class="value text-right">{{ x.UniformaxUsedProducts }} of {{ x.ProductCount }}</td>
              </tr>
            </table>
          </div>

          <div class="flex-1">
            <div class="text-sm font-semibold mb-1">Proximity</div>
            <div *ngIf="!x.ProximityChecked" class="text-sm">
              <div style="color: var(--p-red-500, #ef4444)">The system cannot check Proximity. Select Refresh to try again.</div>
              <div class="text-xs mt-1" style="color: var(--p-text-muted-color, #6b7280)">{{ x.ProximityError }}</div>
            </div>
            <div *ngIf="x.ProximityChecked && x.StylesNotInProximity === x.StyleCount" class="text-sm">
              Proximity does not have {{ x.StyleCount === 1 ? 'this style' : 'these styles' }}.
            </div>
            <table *ngIf="x.ProximityChecked && x.StylesNotInProximity < x.StyleCount" class="qb-info-table compact flush">
              <tr>
                <td class="label">Lines in carts</td>
                <td class="value text-right">{{ x.CartLines }}</td>
              </tr>
              <tr>
                <td class="label">Order lines not placed</td>
                <td class="value text-right">{{ x.UnplacedLines }}</td>
              </tr>
              <tr>
                <td class="label">Kit lines</td>
                <td class="value text-right">{{ x.KitLines }}</td>
              </tr>
              <tr>
                <td class="label">Inventory approvals</td>
                <td class="value text-right">{{ x.InventoryApprovals }}</td>
              </tr>
              <tr>
                <td class="label">Past order lines</td>
                <td class="value text-right">{{ x.HistoryLines }}</td>
              </tr>
              <tr>
                <td class="label">Catalog entries</td>
                <td class="value text-right">{{ x.CatalogEntries }}</td>
              </tr>
              <tr>
                <td class="label">Products used at any time</td>
                <td class="value text-right">{{ x.ProximityUsedProducts }} of {{ x.ProductCount }}</td>
              </tr>
            </table>
          </div>
        </div>
      </ng-container>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ProductExposurePanelComponent implements OnChanges {
  @Input() productTypeId?: number | null;
  @Input() featureChoiceId?: number | null;
  /** Each answer, or null when the count failed. The page locks its meaning controls from it (P3.6). */
  @Output() loaded = new EventEmitter<ProductExposureSummaryDTO | null>();

  x: ProductExposureSummaryDTO | null = null;
  isOpen = false;
  isLoading = false;
  loadError = false;
  /** Only the latest request may fill the panel; a slow earlier one must not overwrite it. */
  private requestNo = 0;

  constructor(private api: ProductExposureApiService) {}

  get noun() {
    return this.featureChoiceId != null ? "This choice" : "This style";
  }

  ngOnChanges() {
    this.refresh();
  }

  async refresh() {
    const no = ++this.requestNo;
    this.isLoading = true;
    this.loadError = false;
    try {
      const x =
        this.featureChoiceId != null
          ? await this.api.getForFeatureChoice(this.featureChoiceId)
          : this.productTypeId != null
            ? await this.api.getForProductType(this.productTypeId)
            : null;
      if (no !== this.requestNo) return;
      this.x = x;
      this.loaded.emit(x);
    } catch {
      // The panel is information only. A failed count must not stop the page, so it is shown here
      // and not passed to the global handler.
      if (no !== this.requestNo) return;
      this.x = null;
      this.loadError = true;
      this.loaded.emit(null);
    } finally {
      if (no === this.requestNo) this.isLoading = false;
    }
  }
}
